import { useEffect, useState } from 'react'
import { useAction, useQuery } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { cn } from '@/lib/utils'
import {
  Cpu, RefreshCw, Server, Loader2, CheckCircle2, XCircle, Clock,
  Play, AlertTriangle, ExternalLink, Film, User,
} from 'lucide-react'

type GpuInstance = {
  id: string
  name: string
  type: string
  state: string
  stateReason?: string
  az: string
  publicIp?: string
  privateIp?: string
  launchTime: number
}

type RenderJob = {
  videoId: string
  workflowId?: string
  shortcode?: string
  title: string
  status: string
  progress?: number
  createdAt: number
  updatedAt: number
  completedAt?: number
  failedAt?: number
  durationSec: number
  aspectRatio?: string
  resolution?: string
  modelId: string
  clipCount: number
  videoUrl?: string
  errorMessage?: string
  userEmail?: string
}

function formatUptime(launchTime: number): string {
  const secs = Math.floor((Date.now() - launchTime) / 1000)
  if (secs < 60) return `${secs}s`
  const mins = Math.floor(secs / 60)
  if (mins < 60) return `${mins}m`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ${mins % 60}m`
  const days = Math.floor(hrs / 24)
  return `${days}d ${hrs % 24}h`
}

function formatDuration(ms: number): string {
  const secs = Math.max(0, Math.floor(ms / 1000))
  if (secs < 60) return `${secs}s`
  const mins = Math.floor(secs / 60)
  return `${mins}m ${secs % 60}s`
}

function useTick(intervalMs = 500) {
  const [, set] = useState(0)
  useEffect(() => {
    const i = setInterval(() => set((n) => n + 1), intervalMs)
    return () => clearInterval(i)
  }, [intervalMs])
}

function phaseFromProgress(p?: number): string {
  if (p == null) return 'QUEUED'
  if (p < 10) return 'PRESTAGE'
  if (p < 85) return 'RENDER'
  if (p < 90) return 'COMBINE'
  if (p < 100) return 'UPLOAD'
  return 'DONE'
}

function ActiveRenderCard({ job }: { job: RenderJob }) {
  useTick(500)
  const elapsedMs = Date.now() - job.createdAt
  const progress = job.progress ?? 0
  const phase = phaseFromProgress(job.progress)
  const isPending = job.status === 'pending' && progress === 0
  // Estimated total time: linearly extrapolate from elapsed/progress, but only
  // after we have some signal (>=10%), otherwise the ETA is wild.
  const etaMs = progress >= 10 && progress < 100
    ? (elapsedMs / progress) * (100 - progress)
    : null

  return (
    <div className="relative bg-gradient-to-b from-amber-950/20 via-zinc-900/40 to-black/40 border border-amber-900/40 rounded-lg overflow-hidden">
      {/* Animated scan line on top */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent animate-pulse" />

      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {isPending ? (
                <Clock className="w-3.5 h-3.5 text-zinc-500" />
              ) : (
                <Play className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
              )}
              <span className="font-mono text-[10px] tracking-[0.2em] text-amber-400">
                {isPending ? 'QUEUED' : phase}
              </span>
              <span className="font-mono text-[10px] text-zinc-600">//</span>
              <span className="font-mono text-[10px] text-zinc-500 tabular-nums">
                {formatDuration(elapsedMs)} elapsed
              </span>
              {etaMs != null && (
                <>
                  <span className="font-mono text-[10px] text-zinc-600">//</span>
                  <span className="font-mono text-[10px] text-zinc-500 tabular-nums">
                    ~{formatDuration(etaMs)} left
                  </span>
                </>
              )}
            </div>
            <div className="font-display text-base font-semibold text-zinc-100 truncate" title={job.title}>
              {job.title || 'Untitled'}
            </div>
          </div>

          {job.shortcode && (
            <a
              href={`/video/${job.shortcode}`}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 inline-flex items-center gap-1 font-mono text-[10px] text-zinc-500 hover:text-zinc-200 tracking-wider"
            >
              {job.shortcode}
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>

        {/* Progress bar */}
        <div className="relative h-1.5 bg-black/60 border border-zinc-900 rounded-sm overflow-hidden">
          <div
            className={cn(
              'absolute inset-y-0 left-0 bg-gradient-to-r transition-[width] duration-500',
              isPending
                ? 'from-zinc-700 to-zinc-600 animate-pulse w-1/4'
                : 'from-amber-500 to-amber-300'
            )}
            style={!isPending ? { width: `${Math.max(2, progress)}%` } : undefined}
          />
          {!isPending && (
            <div className="absolute inset-y-0 right-0 w-8 bg-gradient-to-r from-transparent to-amber-200/30 animate-pulse" />
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 font-mono text-[10px]">
          <Field icon={<User className="w-3 h-3" />} label="USER" value={job.userEmail ?? '—'} mono />
          <Field icon={<Film className="w-3 h-3" />} label="CLIPS" value={`${job.clipCount} · ${job.durationSec.toFixed(0)}s`} />
        </div>
      </div>
    </div>
  )
}

function Field({ icon, label, value, mono = false }: { icon: React.ReactNode; label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <span className="text-zinc-600 shrink-0">{icon}</span>
      <span className="text-zinc-600 shrink-0 tracking-wider">{label}</span>
      <span className={cn('truncate', mono ? 'text-zinc-200' : 'text-zinc-300')} title={value}>
        {value}
      </span>
    </div>
  )
}

function RecentRow({ job }: { job: RenderJob }) {
  const isDone = job.status === 'succeeded'
  const isFailed = job.status === 'failed' || job.status === 'cancelled'

  const totalMs = (job.completedAt ?? job.failedAt ?? job.updatedAt) - job.createdAt
  const ratio = job.durationSec > 0 ? totalMs / 1000 / job.durationSec : 0

  return (
    <div className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-3 items-center px-3 py-1.5 font-mono text-[11px] hover:bg-zinc-900/60 transition-colors">
      <div className="flex items-center w-4 justify-center">
        {isDone ? (
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
        ) : isFailed ? (
          <XCircle className="w-3.5 h-3.5 text-rose-400" />
        ) : (
          <Clock className="w-3.5 h-3.5 text-amber-400" />
        )}
      </div>
      <div className="min-w-0 flex items-center gap-2">
        <span className="truncate text-zinc-200" title={job.title}>{job.title || 'Untitled'}</span>
        {job.shortcode && (
          <a
            href={`/video/${job.shortcode}`}
            target="_blank"
            rel="noreferrer"
            className="text-[10px] text-zinc-600 hover:text-zinc-300 shrink-0"
          >
            {job.shortcode}
          </a>
        )}
        {isFailed && job.errorMessage && (
          <span className="text-[10px] text-rose-400/80 truncate shrink-0" title={job.errorMessage}>
            <AlertTriangle className="w-3 h-3 inline mr-0.5" />
            {job.errorMessage}
          </span>
        )}
      </div>
      <div className="text-zinc-500 tabular-nums text-right min-w-[42px]" title="output duration">
        {job.durationSec > 0 ? `${job.durationSec.toFixed(0)}s` : '—'}
      </div>
      <div className="text-zinc-500 tabular-nums text-right min-w-[64px]" title="resolution">
        {job.resolution ?? '—'}
      </div>
      <div className={cn(
        'tabular-nums text-right min-w-[70px]',
        isFailed ? 'text-rose-300' : 'text-zinc-300'
      )} title="render wall-clock">
        {formatDuration(totalMs)}
      </div>
      <div className="text-zinc-500 tabular-nums text-right min-w-[80px]" title="render time / output duration">
        {ratio > 0 && isDone ? `${ratio.toFixed(1)}× real` : '—'}
      </div>
    </div>
  )
}

function WorkerStrip({ instances }: { instances: GpuInstance[] | null }) {
  const running = instances?.filter((i) => i.state === 'running') ?? []
  const primary = running[0]
  useTick(1000)

  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-md border border-zinc-800 bg-zinc-950 font-mono text-[10px]">
      <div className="flex items-center gap-2">
        <div className={cn(
          'w-2 h-2 rounded-full',
          primary
            ? 'bg-emerald-400 shadow-[0_0_8px_1px_rgba(52,211,153,0.9)]'
            : 'bg-rose-500 shadow-[0_0_8px_1px_rgba(244,63,94,0.7)]'
        )} />
        <span className="tracking-[0.2em] text-zinc-400">
          {primary ? 'WORKER ONLINE' : 'NO WORKER'}
        </span>
      </div>
      {primary && (
        <>
          <div className="w-px h-4 bg-zinc-800" />
          <div className="flex items-center gap-1 text-zinc-500">
            <Server className="w-3 h-3" />
            <span className="text-zinc-300">{primary.type}</span>
          </div>
          <div className="w-px h-4 bg-zinc-800" />
          <span className="text-zinc-500">{primary.publicIp ?? '—'}</span>
          <div className="w-px h-4 bg-zinc-800" />
          <span className="text-zinc-500">{formatUptime(primary.launchTime)}</span>
          {instances && instances.length > 1 && (
            <>
              <div className="w-px h-4 bg-zinc-800" />
              <span className="text-zinc-500">+{instances.length - 1} others</span>
            </>
          )}
        </>
      )}
    </div>
  )
}

export default function RenderGpusPage() {
  const renders = useQuery(api.renderGpusQueries.listRenders, {})
  const listInstances = useAction(api.renderGpus.list)
  const [instances, setInstances] = useState<GpuInstance[] | null>(null)
  const [instErr, setInstErr] = useState<string | null>(null)
  const [instLoading, setInstLoading] = useState(false)

  async function refreshInstances() {
    setInstLoading(true)
    setInstErr(null)
    try {
      setInstances(await listInstances({}))
    } catch (e: any) {
      setInstErr(e?.message ?? String(e))
    } finally {
      setInstLoading(false)
    }
  }

  useEffect(() => {
    refreshInstances()
    const i = setInterval(refreshInstances, 30_000)
    return () => clearInterval(i)
  }, [])

  const active = renders?.active ?? []
  const recent = renders?.recent ?? []

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-950 via-black to-zinc-950 text-zinc-200">
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      <div className="relative max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between gap-6 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Cpu className="w-4 h-4 text-emerald-400" />
              <span className="font-mono text-[10px] tracking-[0.25em] text-emerald-400">RENDER_QUEUE</span>
              <span className="font-mono text-[10px] text-zinc-600">//</span>
              <span className="font-mono text-[10px] tracking-wider text-zinc-500">us-east-2</span>
            </div>
            <h1 className="text-3xl font-semibold tracking-tight text-zinc-100">Render queue</h1>
            <p className="text-sm text-zinc-500 mt-1">
              Active exports stream live. Completed renders appear below.
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <WorkerStrip instances={instances} />
            <button
              onClick={refreshInstances}
              disabled={instLoading}
              title="Refresh worker status"
              className={cn(
                'h-8 w-8 flex items-center justify-center rounded-md border transition-colors',
                'bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:bg-zinc-800',
                instLoading && 'opacity-50'
              )}
            >
              {instLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            </button>
          </div>
        </div>

        {instErr && (
          <div className="mb-4 p-2 bg-rose-950/40 border border-rose-900 rounded-md text-xs text-rose-300 font-mono">
            Worker fetch failed: {instErr}
          </div>
        )}

        {/* ACTIVE section */}
        <section className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-px flex-1 bg-zinc-800" />
            <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-600">
              ACTIVE · {active.length}
            </span>
            <div className="h-px flex-1 bg-zinc-800" />
          </div>

          {renders === undefined ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-zinc-700" />
            </div>
          ) : active.length === 0 ? (
            <div className="text-center py-10 border border-dashed border-zinc-900 rounded-lg">
              <div className="font-mono text-[11px] tracking-[0.15em] text-zinc-600">IDLE · NO ACTIVE RENDERS</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {active.map((job) => <ActiveRenderCard key={job.videoId} job={job} />)}
            </div>
          )}
        </section>

        {/* RECENT section */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <div className="h-px flex-1 bg-zinc-800" />
            <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-600">
              RECENT · {recent.length}
            </span>
            <div className="h-px flex-1 bg-zinc-800" />
          </div>

          <div className="bg-gradient-to-b from-zinc-900/30 to-black/30 border border-zinc-800 rounded-lg overflow-hidden">
            <div className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-3 px-3 py-2 border-b border-zinc-800 bg-black/40 font-mono text-[9px] tracking-[0.2em] text-zinc-600">
              <div className="w-4" />
              <div>NAME</div>
              <div className="text-right min-w-[42px]">LEN</div>
              <div className="text-right min-w-[64px]">RES</div>
              <div className="text-right min-w-[70px]">RENDER</div>
              <div className="text-right min-w-[80px]">RATIO</div>
            </div>
            {recent.length > 0 ? (
              <div className="divide-y divide-zinc-900">
                {recent.map((job) => <RecentRow key={job.videoId} job={job} />)}
              </div>
            ) : renders === undefined ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-4 h-4 animate-spin text-zinc-700" />
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-600 font-mono text-xs">
                NO EXPORTS
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}
